import StatCard from '@/components/Views/StatCard/StatCard';
import Chart from '@/components/Ui/Chart/Chart';
import { ChartType } from '@/components/Ui/Chart/Chart.types';
import DoughnutChart from '@/components/Ui/DoughnutChart/DoughnutChart';
import LegendDoughnutChart from '@/components/Ui/DoughnutChart/LegendDoughnutChart';
import SelectDropDown from '@/components/Common/Selects/SelectDropDown';
import { useDashboard } from './Dashboard.hook';
import { memo, useMemo, useCallback } from 'react';

type StatisticsDoughnutProps = {
  title: string;
  statisticsData: { values: number[]; labels: string[] };
  selectedType: string;
  colors: string[];
  percentage: { percentage: number }[];
};

const StatisticsDoughnut = memo(
  ({
    title,
    statisticsData,
    selectedType,
    colors,
    percentage,
  }: StatisticsDoughnutProps) => {
    const labels = useMemo(
      () => statisticsData?.values?.map((item) => item.toString()) || [],
      [statisticsData?.values],
    );

    const legendItems = useMemo(
      () =>
        statisticsData?.labels?.map((item, index) => ({
          label: item.toString(),
          value:
            selectedType === 'offre'
              ? `${statisticsData.values[index] * 0} frs`
              : statisticsData.values[index],
          color: colors[index],
          percentageOrSalesCount:
            selectedType === 'offre'
              ? `${statisticsData.values[index]} vente${
                  statisticsData.values[index] > 1 ? 's' : ''
                }`
              : `${percentage?.[index]?.percentage}%`,
        })) || [],
      [statisticsData, selectedType, colors, percentage],
    );

    const centerText = useMemo(
      () => ({ title: 'Répartition', subtitle: `par ${title}` }),
      [title],
    );

    return (
      <div className="h-full">
        <DoughnutChart
          styleDoughnutChart="p-0 m-0 text-white -top-[30px]"
          centerText={centerText}
          labels={labels}
          backgroundColor={colors}
        />
        <LegendDoughnutChart styleLegend="mt-[35px]" items={legendItems} />
      </div>
    );
  },
  // une fonction de comparaison personnalisée
  (prevProps, nextProps) => {
    return (
      prevProps.title === nextProps.title &&
      prevProps.selectedType === nextProps.selectedType &&
      JSON.stringify(prevProps.statisticsData) ===
        JSON.stringify(nextProps.statisticsData) &&
      JSON.stringify(prevProps.colors) === JSON.stringify(nextProps.colors) &&
      JSON.stringify(prevProps.percentage) ===
        JSON.stringify(nextProps.percentage)
    );
  },
);

StatisticsDoughnut.displayName = 'StatisticsDoughnut';

type DoughnutHeaderProps = {
  title: string;
  description: string;
  filterOptions: { _id: string }[];
  selectedType: string;
  setSelectedType: (type: string) => void;
};

const DoughnutHeader = memo(
  ({
    title,
    description,
    filterOptions,
    selectedType,
    setSelectedType,
  }: DoughnutHeaderProps) => {
    const handleChange = useCallback(
      (option: { _id: string }) => setSelectedType(option._id),
      [setSelectedType],
    );

    return (
      <div className="flex justify-between pt-5 pb-0 mb-0">
        <div className="text-white">
          <h5>{title}</h5>
          <p className="text-sm text-textColor w-[141px]">{description}</p>
        </div>
        <div>
          <SelectDropDown
            options={filterOptions}
            initialSelectedOptions={
              filterOptions.find((option) => option._id === selectedType) ||
              filterOptions[0]
            }
            placeholder="Offre"
            name="filterType"
            handleChange={(option) => handleChange(option as { _id: string })}
            className="px-4 text-white"
          />
        </div>
      </div>
    );
  },
);

DoughnutHeader.displayName = 'DoughnutHeader';

const DashboardStats = memo(() => {
  const {
    regionData,
    seriesData,
    selectedType,
    setSelectedType,
    statisticsData,

    cards,
    filterOptions,
    title,
    description,
    percentage,
    colors,
  } = useDashboard();

  // Mémoriser les cartes statistiques
  const statisticsCards = useMemo(
    () => (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card, index) => (
          <StatCard key={index} {...card} />
        ))}
      </div>
    ),
    [cards],
  );

  // Mémoriser le graphique en donut
  const doughnutSection = useMemo(
    () => (
      <div className="w-[277px] h-[430px] bg-black px-4">
        <DoughnutHeader
          title={title}
          description={description}
          filterOptions={filterOptions}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
        />
        {statisticsData && colors && percentage && (
          <StatisticsDoughnut
            title={title}
            statisticsData={statisticsData}
            selectedType={selectedType}
            colors={colors}
            percentage={percentage}
          />
        )}
      </div>
    ),
    [
      title,
      description,
      filterOptions,
      selectedType,
      statisticsData,
      colors,
      percentage,
      setSelectedType,
    ],
  );

  // Mémoriser les sections de graphiques
  const chartSections = useMemo(
    () => (
      <div>
        <ChartSection
          title="Croissance des utilisateurs"
          description="Nombre d'utilisateur total par série"
          data={seriesData?.values || []}
          labels={seriesData?.labels || []}
        />
        <ChartSection
          title="Croissance des utilisateurs"
          description="Nombre d'utilisateur total par région"
          data={regionData?.values || []}
          labels={regionData?.labels || []}
        />
      </div>
    ),
    [seriesData, regionData],
  );

  return (
    <div className="flex flex-col max-h-screen">
      <div className="py-4">{statisticsCards}</div>
      <div className="w-full grid grid-cols-1 lg:grid-cols-[277px_1fr] items-start gap-4">
        {doughnutSection}
        {chartSections}
      </div>
    </div>
  );
});

DashboardStats.displayName = 'DashboardStats';

export default DashboardStats;

const ChartSection = memo(
  ({
    title,
    description,
    data,
    labels,
  }: {
    title: string;
    description: string;
    data: number[];
    labels: string[];
  }) => {
    return (
      <div className="w-full bg-black pt-4 mb-[18.22px]">
        <div className="pl-4">
          <p className="text-white text-[15.102px] font-normal tracking-[-0.519px]">
            {title}
          </p>
          <p className="text-textColor text-xs font-normal tracking-[-0.519px]">
            {description}
          </p>
        </div>
        <Chart stepSize={1} type={ChartType.BAR} data={data} labels={labels} />
      </div>
    );
  },
);

ChartSection.displayName = 'ChartSection';
